import { sha256 } from '@noble/hashes/sha256';

const URL_SAFE_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

export interface PkcePair {
  codeVerifier: string;
  codeChallenge: string;
  /** Always 'S256' — we don't support the deprecated 'plain' transform. */
  codeChallengeMethod: 'S256';
}

/** Generate a cryptographically random URL-safe string. */
export function randomUrlSafeString(length = 64): string {
  if (length < 43 || length > 128) {
    // RFC 7636 §4.1: code_verifier MUST have a length of 43..128 chars.
    throw new RangeError(`PKCE code verifier length must be 43..128, got ${length}`);
  }
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    // biased modulo is fine here — we only need unpredictability, not uniformity
    out += URL_SAFE_ALPHABET[bytes[i]! % URL_SAFE_ALPHABET.length];
  }
  return out;
}

/** Generate a code verifier + S256 code challenge pair. */
export function generatePkcePair(): PkcePair {
  const codeVerifier = randomUrlSafeString(64);
  const codeChallenge = s256(codeVerifier);
  return { codeVerifier, codeChallenge, codeChallengeMethod: 'S256' };
}

function s256(input: string): string {
  const digest = sha256(new TextEncoder().encode(input));
  return base64UrlEncode(digest);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  // btoa is part of the WHATWG spec; available in Node 18+, browsers, and RN.
  const b64 = globalThis.btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
